import { useLocation, Link } from "react-router-dom";
import { Check } from "lucide-react";
import { cn } from "@headerhawk/ui";
import {
  useOnboardingStore,
  type OnboardingStep,
} from "@/lib/stores/onboarding-store";

export interface StepDefinition {
  id: OnboardingStep;
  label: string;
  path: string;
}

interface ProgressIndicatorProps {
  steps: StepDefinition[];
}

interface StepIndicatorProps {
  step: StepDefinition;
  index: number;
  isCurrent: boolean;
  isCompleted: boolean;
  isAccessible: boolean;
}

function StepIndicator({
  step,
  index,
  isCurrent,
  isCompleted,
  isAccessible,
}: StepIndicatorProps) {
  const content = (
    <div className="flex flex-col items-center gap-1">
      <div
        className={cn(
          "flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-medium transition-colors",
          isCompleted && "border-primary bg-primary text-primary-foreground",
          isCurrent &&
            !isCompleted &&
            "border-primary bg-background text-primary",
          !isCurrent &&
            !isCompleted &&
            "border-muted bg-background text-muted-foreground",
        )}
      >
        {isCompleted ? <Check className="h-4 w-4" /> : index}
      </div>
      <span
        className={cn(
          "text-xs font-medium",
          isCurrent || isCompleted
            ? "text-foreground"
            : "text-muted-foreground",
        )}
      >
        {step.label}
      </span>
    </div>
  );

  if (isAccessible && isCompleted) {
    return (
      <Link to={step.path} className="cursor-pointer">
        {content}
      </Link>
    );
  }

  return content;
}

export function ProgressIndicator({ steps }: ProgressIndicatorProps) {
  const location = useLocation();
  const { isStepCompleted, canAccessStep } = useOnboardingStore();

  return (
    <nav aria-label="Onboarding progress">
      <ol className="flex items-center justify-center gap-2 sm:gap-4">
        {steps.map((step, index) => {
          const isCurrent = location.pathname === step.path;
          const isCompleted = isStepCompleted(step.id);
          const isAccessible = canAccessStep(step.id);

          return (
            <li key={step.id} className="flex items-center gap-2 sm:gap-4">
              {index > 0 && (
                <div
                  className={cn(
                    "h-px w-6 sm:w-12",
                    isCompleted || isCurrent ? "bg-primary" : "bg-muted",
                  )}
                />
              )}
              <StepIndicator
                step={step}
                index={index + 1}
                isCurrent={isCurrent}
                isCompleted={isCompleted}
                isAccessible={isAccessible}
              />
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
